(() => {
  if (customElements.get('phc-deferred-reviews')) return;

  const moduleLoads = new Map();
  const cssLoads = new Map();

  function withTimeout(promise) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Customer reviews took too long to load.')), 12000);
      promise.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
    });
  }

  function coreReady() {
    if (typeof window.jdgm?.$ === 'function') return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        document.removeEventListener('jdgm.finishLoadingCore', ready);
        reject(new Error('Review core did not finish loading.'));
      }, 10000);
      function ready() {
        clearTimeout(timeout);
        resolve();
      }
      document.addEventListener('jdgm.finishLoadingCore', ready, { once: true });
    });
  }

  function loadCss(url) {
    if (cssLoads.has(url)) return cssLoads.get(url);
    const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(link => link.href === url);
    if (existing) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = resolve;
      link.onerror = () => reject(new Error('Review styles could not load.'));
      document.head.appendChild(link);
    });
    cssLoads.set(url, promise);
    return promise;
  }

  async function widgetStyles(base, entryKey) {
    const response = await fetch(new URL('manifest.json', base), { credentials: 'omit' });
    if (!response.ok) throw new Error('Review styles manifest could not load.');
    const manifest = await response.json();
    const seen = new Set();
    const files = new Set();
    function visit(key) {
      if (seen.has(key)) return;
      seen.add(key);
      const entry = manifest[key];
      if (!entry) return;
      (entry.css || []).forEach(file => files.add(new URL(file, base).href));
      (entry.imports || []).forEach(visit);
    }
    if (!manifest[entryKey]) throw new Error('Review widget entry is unavailable.');
    visit(entryKey);
    await Promise.all(Array.from(files, loadCss));
  }

  function loadModule(url) {
    if (moduleLoads.has(url)) return moduleLoads.get(url);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = url;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Review widget could not load.'));
      document.head.appendChild(script);
    });
    moduleLoads.set(url, promise);
    return promise;
  }

  class PHCDeferredReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.dataset.eager === 'true') return;
      this.initialized = true;
      if (window.PHCReviews?.isDirectRequest() || !('IntersectionObserver' in window)) {
        this.load();
      } else {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.load();
        }, { rootMargin: '150px 0px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    load() {
      if (this.dataset.eager === 'true') return Promise.resolve();
      if (this.loading) return this.loading;
      this.observer?.disconnect();
      this.dataset.state = 'loading';
      this.setAttribute('aria-busy', 'true');
      this.loading = withTimeout((async () => {
        const widget = window.PHCReviews?.mount(this);
        if (!widget) throw new Error('Review widget markup is unavailable.');
        await coreReady();
        const base = window.jdgm?.CDN_BASE_URL;
        if (!base || !widget.dataset.entryPoint || !widget.dataset.entryKey) throw new Error('Review widget configuration is unavailable.');
        await widgetStyles(base, widget.dataset.entryKey);
        await loadModule(new URL(widget.dataset.entryPoint, base).href);
        this.dataset.state = 'ready';
        this.setAttribute('aria-busy', 'false');
        this.querySelector('[data-phc-review-placeholder]').hidden = true;
      })()).catch(error => {
        this.dataset.state = 'error';
        this.setAttribute('aria-busy', 'false');
        this.querySelector('[data-phc-review-status]').textContent = 'Customer reviews are temporarily unavailable.';
        this.querySelector('[data-phc-review-fallback]').hidden = false;
        console.error('Customer reviews could not load:', error);
      });
      return this.loading;
    }

    async open() {
      await this.load();
      const target = this.querySelector('#judgeme_product_reviews') || this;
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  }

  customElements.define('phc-deferred-reviews', PHCDeferredReviews);

  function isReviewLink(target) {
    if (target.closest('product-info .jdgm-prev-badge')) return true;
    const link = target.closest('a[href]');
    if (!link) return false;
    const url = new URL(link.href, location.href);
    return url.origin === location.origin && url.pathname === location.pathname && url.hash === '#judgeme_product_reviews';
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const reviews = document.querySelector('phc-deferred-reviews');
    if (!reviews || reviews.dataset.eager === 'true' || !isReviewLink(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    reviews.open();
  }, true);

  document.addEventListener('keydown', event => {
    if (!(event.target instanceof Element)) return;
    if (!['Enter', ' '].includes(event.key) || !event.target.closest('product-info .jdgm-prev-badge')) return;
    const reviews = document.querySelector('phc-deferred-reviews');
    if (!reviews || reviews.dataset.eager === 'true') return;
    event.preventDefault();
    event.stopPropagation();
    reviews.open();
  }, true);

  window.addEventListener('hashchange', () => {
    if (!['#judgeme', '#judgeme_product_reviews'].includes(location.hash)) return;
    const reviews = document.querySelector('phc-deferred-reviews');
    if (reviews && reviews.dataset.eager !== 'true') reviews.open();
  });
})();

